import { cva } from 'class-variance-authority'

export type CardSize = 'small' | 'medium' | 'large'

export type CardType = 'article' | 'book' | 'journal' | 'author'

export type CardOrientation = 'horizontal' | 'vertical'
export interface CardProps {
  type: CardType
  size?: CardSize
  presentation?: boolean
  orientation?: CardOrientation
}

export const cardVariants = {
  card: {
    base: cva([
      'group',
      'flex',
      'no-underline',
      'rounded-4xl',
      'ease-in-out',
      'transition',
      'duration-300',
      'outline',
      'focus-visible:outline',
    ], {
      variants: {
        type: {
          'article': [
            'p-l',
            'items-start',
            'bg-white',
            'flex-col',
            'gap-m',
            'focus:outline-blurple-300',
            'focus:outline-2',
            'hover:bg-surface-hover',
          ],
          'book': [
            'pt-xl',
            'px-l',
            'pb-l',
            'flex-col',
            'items-center',
            'bg-surface-primary',
            'focus-visible:outline-blurple-300',
            'focus-visible:outline-2',
            'gap-m',
            '[&:has([id^=favorite-btn]:hover)]:bg-surface-primary',
          ],
          'author': [
            'focus-visible:outline',
            'focus-visible:outline-blurple-300',
            'focus-visible:outline-2',
            'items-center',
          ],
          'journal': [],
        },
        size: {
          small: [],
          medium: [],
          large: [],
        },
        presentation: {
          true: [],
          false: [],
        },
        orientation: {
          horizontal: [],
          vertical: [],
        },
      },
      compoundVariants: [
        {
          type: 'author',
          size: ['small', 'medium', 'large'],
          orientation: 'vertical',
          presentation: false,
          class: ['w-full', 'flex-col', 'items-center', 'gap-s', 'px-m', 'pt-m', 'pb-l', 'bg-surface-primary', 'hover:bg-surface-hover'],
        },
        {
          type: 'author',
          size: ['small', 'medium', 'large'],
          orientation: 'horizontal',
          presentation: false,
          class: ['w-full', 'max-w-[886px]', 'h-auto', 'gap-m', 'pl-m', 'pt-m', 'pr-xl', 'pb-l', 'bg-surface-primary', 'hover:bg-surface-hover'],
        },
        {
          type: 'author',
          size: ['small', 'medium', 'large'],
          presentation: true,
          orientation: 'vertical',
          class: ['w-[358px]', 'lg:w-[392px]', '2xl:w-[452px]', 'h-[434px]', 'lg:h-[460px]', '2xl:h-[571px]', 'flex-col', 'items-center', 'justify-center', 'gap-s', 'bg-moss-500', 'px-l', '2xl:px-20', 'py-xl', '2xl:py-24'],
        },
        {
          type: ['book', 'journal', 'article'],
          size: ['small', 'large', 'medium'],
          presentation: false,
          class: ['w-[317px]', 'gap-l', 'hover:bg-surface-hover'],
        },
        {
          type: 'book',
          size: 'small',
          presentation: true,
          class: ['w-[326px]', 'gap-m', 'focus:border-0', 'focus:border-transparent'],
        },
        {
          type: 'book',
          size: 'medium',
          presentation: true,
          class: ['w-[392px]', 'gap-m', 'focus:border-0', 'focus:border-transparent'],
        },
        {
          type: 'book',
          size: 'large',
          presentation: true,
          class: ['w-[326px]', 'sm:w-[392px]', 'xl:w-[493px]', 'h-[326px]', 'sm:h-[392px]', 'xl:h-[493px]', 'gap-m', 'focus:border-0', 'focus:border-transparent', 'justify-center'],
        },
        {
          type: 'journal',
          size: 'large',
          presentation: true,
          class: ['w-[326px]', 'sm:w-[392px]', 'xl:w-[493px]'],
        },
      ],
    }),
  },

  image: cva([], {
    variants: {
      type: {
        article: [],
        book: [],
        author: ['w-[224px]', 'aspect-square', 'object-cover', 'mix-blend-multiply'],
      },
      size: {
        small: [],
        medium: [],
        large: [],
      },
      presentation: {
        true: [],
        false: [],
      },
    },
    compoundVariants: [
      {
        type: 'book',
        size: ['small', 'large', 'medium'],
        presentation: false,
        class: ['w-[116px]', 'h-[163px]', 'rounded-xl'],
      },
      {
        type: 'book',
        size: 'small',
        presentation: true,
        class: ['w-[132px]', 'h-[187px]', 'rounded'],
      },
      {
        type: 'book',
        size: 'medium',
        presentation: true,
        class: ['w-[194px]', 'h-[275px]', 'rounded', 'shrink-0'],
      },
      {
        type: 'book',
        size: 'large',
        presentation: true,
        class: ['w-[132px]', 'sm:w-[194px]', 'xl:w-[230px]', 'h-[187px]', 'sm:h-[275px]', 'xl:h-[326px]', 'rounded'],
      },
    ],
  }),

  title: cva([], {
    variants: {
      type: {
        article: [],
        book: [],
        author: [],
      },
      presentation: {
        true: [],
        false: [],
      },
    },
    compoundVariants: [
      {
        type: 'book',
        class: ['text-carbon-500', 'dark:text-carbon-50'],
      },
      {
        type: 'article',
        class: ['text-surface-invert'],
      },
      {
        type: 'author',
        class: ['text-center'],
      },
      {
        type: 'author',
        presentation: true,
        class: ['text-moss-200', 'text-center'],
      },
    ],
  }),

  framed: cva([
    'w-52',
    'sm:w-60',
  ]),
}
